// 스킬 연계(LINK) 화면 층(설계도 combat-skill-link, 규칙 rule-skill-link). sim이 정한 view.link와 Ev.LinkUp/LinkFinish를 읽기만 한다.
// - 도장: 오른쪽 콤보 숫자 바로 아래의 기울어진 진홍·금빛 "LINK x2 / +20%"(작게), "LINK x3 / FINISH! +35%"(크게, 금빛 불똥). 배율 숫자는 LINK.damage(또는 이벤트 c)에서 만든다.
// - 가장자리 속도선: 연계 3단 피니시 순간 화면 네 가장자리(특히 모서리)에만 붉은 선이 약 300ms. 가운데·영웅·스킬 버튼 위는 비운다(마스크).
//   섬광 약화(settings.reduceFlash)면 내지 않고, 섬광 안전 규칙(FLASH_SAFETY.minGapMs)보다 촘촘하게 내지 않는다.
// 프레임 루프에서는 값이 바뀔 때만 DOM을 쓴다(문자열은 이벤트 때만 만든다). 효과 Animation은 생성자에서 한 번 만든다.
import { FLASH_SAFETY, LINK } from '../shared/config';
import { Ev, type GameEvent } from '../shared/events';
import type { GameView } from '../shared/types';
import { FONT, FONT_NUM, GOLD_TEXT, el } from './dom';

/** 도장이 뜨는 최소 단계(1단·끊김에는 도장이 없다) */
export const LINK_MIN_STAGE = 2;
/** 창이 닫히기 직전 흐려지는 구간(틱, 0.3초) */
const FADE_TICKS = 18;
/** 도장 위치: 콤보 상자 윗변(PC 252px·터치 212px) 아래로 콤보 숫자 높이(88)만큼 + 여백. PC는 칭찬 문구(콤보 아래 약 50px)를 비킨다. 디자인 px, 화면 배율을 곱한다 */
const BELOW_COMBO = 88;
const GAP_PC = 64;
const GAP_TOUCH = 4;
const OPACITY: readonly string[] = Array.from({ length: 21 }, (_, i) => String(i / 20));

/** 배율(예: 1.35) → 보너스 퍼센트(35). 이벤트의 c가 있으면 그 값을, 없으면 LINK.damage 표를 쓴다 */
export function linkBonusPercent(stage: number, mult?: number): number {
  const m = mult !== undefined && Number.isFinite(mult) && mult > 1 ? mult : (LINK.damage[Math.min(Math.max(stage, 1), LINK.maxStage) - 1] ?? 1);
  return Math.round((m - 1) * 100);
}
/** 도장 큰 글자("LINK x3") */
export function linkTitle(stage: number): string {
  return 'LINK x' + stage;
}
/** 도장 작은 줄: 3단은 "FINISH! +35%", 2단은 "+20%" */
export function linkSub(stage: number, mult?: number): string {
  const b = '+' + linkBonusPercent(stage, mult) + '%';
  return stage >= LINK.maxStage ? 'FINISH! ' + b : b;
}
/** 닫히기 직전 불투명도(0..20 단계). 스킬 사용 중에는 타이머가 줄지 않아 항상 20 */
export function linkOpacityStep(timer: number): number {
  return timer >= FADE_TICKS ? 20 : Math.max(7, Math.round((0.35 + (Math.max(0, timer) / FADE_TICKS) * 0.65) * 20));
}
/** 표시할 단계: 전투 중이고 2단 이상일 때만, 그 밖에는 0 */
export function linkShownStage(live: boolean, stage: number): number {
  return live && stage >= LINK_MIN_STAGE ? Math.min(stage, LINK.maxStage) : 0;
}

const RED_SLAB = 'linear-gradient(95deg,rgba(60,0,10,0) 0%,rgba(150,8,26,.92) 16%,rgba(96,0,16,.95) 72%,rgba(50,0,8,0) 100%)';
const BRUSH = 'polygon(0 40%,5% 14%,20% 20%,38% 2%,60% 12%,82% 0,100% 30%,97% 70%,80% 100%,56% 88%,32% 100%,10% 84%)';
const TITLE_CSS = 'font-family:' + FONT_NUM + ';font-weight:900;font-style:italic;letter-spacing:1px;line-height:1;white-space:nowrap;' + GOLD_TEXT +
  '-webkit-text-stroke:2px #4a0a06;filter:drop-shadow(0 3px 0 #5a1000) drop-shadow(0 0 10px rgba(255,170,40,.75));';
const SUB_CSS = 'font-family:' + FONT_NUM + ';font-weight:900;font-style:italic;letter-spacing:1px;line-height:1.05;white-space:nowrap;margin-top:-2px;' +
  'background:linear-gradient(180deg,#ffffff 0%,#ffe9b0 55%,#ffb43a 100%);-webkit-background-clip:text;background-clip:text;color:transparent;' +
  '-webkit-text-stroke:1.5px #4a0a06;filter:drop-shadow(0 2px 0 #5a1000) drop-shadow(0 0 6px rgba(255,120,40,.6));';
/** 단계별 글자 크기(px): 2단 작게, 3단 크게 */
const TITLE_PX = [0, 0, 34, 58];
const SUB_PX = [0, 0, 22, 28];

/** 불똥 시작 위치(도장 상자 대비 %)와 퍼지는 거리(px) */
const SPARKS: readonly (readonly [number, number, number, number])[] = [
  [12, 40, -30, -26], [28, 18, -10, -40], [46, 62, 8, 34], [62, 22, 14, -38],
  [78, 56, 36, 20], [90, 30, 40, -20], [22, 78, -22, 30], [70, 84, 20, 36],
];
const SPARK_FRAMES = (dx: number, dy: number): Keyframe[] => [
  { opacity: 0, transform: 'translate(0,0) scale(.4)' },
  { opacity: 1, transform: 'translate(' + dx * 0.4 + 'px,' + dy * 0.4 + 'px) scale(1)', offset: 0.25 },
  { opacity: 0, transform: 'translate(' + dx + 'px,' + dy + 'px) scale(.3)' },
];
const POP_2: Keyframe[] = [
  { opacity: 0, transform: 'scale(1.7) rotate(-6deg)' },
  { opacity: 1, transform: 'scale(.95)', offset: 0.55 },
  { opacity: 1, transform: 'scale(1)' },
];
const POP_3: Keyframe[] = [
  { opacity: 0, transform: 'scale(2.3) translateX(24px)' },
  { opacity: 1, transform: 'scale(.92)', offset: 0.45 },
  { opacity: 1, transform: 'scale(1.06)', offset: 0.7 },
  { opacity: 1, transform: 'scale(1)' },
];
/** 동작 줄이기: 크기·이동 없이 불투명도만 */
const POP_CALM: Keyframe[] = [{ opacity: 0 }, { opacity: 1 }];
const EDGE_FRAMES: Keyframe[] = [{ opacity: 0 }, { opacity: 0.8, offset: 0.2 }, { opacity: 0 }];
/** 속도선: 중심에서 퍼지는 가는 붉은 선 두 겹. 마스크로 가운데 약 2/3(영웅·버튼 자리)를 비우고 모서리 쪽에서만 보인다 */
const EDGE_BG = 'repeating-conic-gradient(from 0deg at 50% 50%,rgba(230,40,40,.55) 0deg .55deg,rgba(230,40,40,0) .55deg 3.1deg),' +
  'repeating-conic-gradient(from 1.3deg at 50% 50%,rgba(255,90,60,.4) 0deg .4deg,rgba(255,90,60,0) .4deg 4.7deg)';
const EDGE_MASK = 'radial-gradient(ellipse farthest-corner at 50% 50%,transparent 0%,transparent 60%,#000 100%),' +
  'linear-gradient(90deg,#000 0%,transparent 6%,transparent 94%,#000 100%)';

function prepare(target: Element, frames: Keyframe[], duration: number, easing: string): Animation {
  return new Animation(new KeyframeEffect(target, frames, { duration, easing }), document.timeline);
}
function restart(a: Animation): void {
  a.currentTime = 0;
  a.play();
}

export class LinkHud {
  private readonly edge: HTMLDivElement;
  private readonly box: HTMLDivElement;
  private readonly title: HTMLSpanElement;
  private readonly sub: HTMLSpanElement;
  private readonly sparks: HTMLDivElement[] = [];
  private readonly sparkAnims: Animation[] = [];
  private readonly pops: Animation[];
  private readonly edgeAnim: Animation;
  private readonly motionQuery: MediaQueryList | null;
  private readonly baseTop: number;
  private readonly gap: number;
  /** 지금 보이는 단계(0 = 숨김) */
  private shown = 0;
  private lastScale = -1;
  private lastOp = -1;
  private lastEdgeMs = -1e9;

  constructor(parent: HTMLElement, private readonly view: GameView, touch: boolean) {
    this.baseTop = touch ? 212 : 252;
    this.gap = touch ? GAP_TOUCH : GAP_PC;
    this.motionQuery = typeof matchMedia === 'function' ? matchMedia('(prefers-reduced-motion: reduce)') : null;
    // 속도선 층은 HUD의 맨 아래에 깐다(HUD 요소 아래, 3D 화면 위)
    this.edge = el('div', 'position:absolute;inset:0;opacity:0;pointer-events:none;background:' + EDGE_BG + ';-webkit-mask-image:' + EDGE_MASK + ';mask-image:' + EDGE_MASK + ';');
    this.edge.dataset.testid = 'link-edge';
    parent.insertBefore(this.edge, parent.firstChild);
    this.edgeAnim = prepare(this.edge, EDGE_FRAMES, 300, 'linear');

    this.box = el('div', 'position:absolute;left:50%;top:max(6px,env(safe-area-inset-top));height:28px;max-width:calc(100% - 16px);overflow:hidden;transform:translateX(-50%);transform-origin:50% 0;display:none;pointer-events:none;', parent);
    this.box.dataset.testid = 'link-stamp';
    const pop = el('div', 'position:relative;transform-origin:50% 50%;', this.box);
    const tilt = el('div', 'position:relative;isolation:isolate;display:flex;align-items:center;gap:8px;height:28px;box-sizing:border-box;padding:0 12px;border:1px solid #bb9e56;border-radius:6px;background:#102037e8;', pop);
    el('div', 'position:absolute;left:-18px;right:-10px;top:6%;height:92%;z-index:-1;background:' + RED_SLAB + ';clip-path:' + BRUSH + ';', tilt);
    this.title = el('span', TITLE_CSS, tilt, '');
    this.sub = el('span', SUB_CSS, tilt, '');
    // 3단 피니시 불똥(작은 금빛 마름모)
    const layer = el('div', 'position:absolute;right:0;top:0;width:260px;height:96px;pointer-events:none;', this.box);
    for (const [x, y, dx, dy] of SPARKS) {
      const s = el('div', 'position:absolute;left:' + x + '%;top:' + y + '%;width:7px;height:7px;margin:-3px 0 0 -3px;background:linear-gradient(135deg,#fff6c8,#ffb43a);transform:rotate(45deg);opacity:0;box-shadow:0 0 6px 1px rgba(255,170,40,.8);', layer);
      this.sparks.push(s);
      this.sparkAnims.push(prepare(s, SPARK_FRAMES(dx, dy), 520, 'ease-out'));
    }
    this.pops = [prepare(pop, POP_2, 240, 'ease-out'), prepare(pop, POP_3, 360, 'ease-out'), prepare(pop, POP_CALM, 150, 'linear')];
  }

  /** 단계에 맞춰 글자·크기를 쓴다(이벤트나 단계가 바뀔 때만 부른다) */
  private render(stage: number, mult: number | undefined): void {
    this.title.textContent = '연계 ' + stage + '단';
    this.sub.textContent = (stage >= LINK.maxStage ? '마무리 ' : '') + '+' + linkBonusPercent(stage, mult) + '%';
    this.title.style.cssText = 'font:900 15px ' + FONT + ';color:#ffe5a0;white-space:nowrap;';
    this.sub.style.cssText = 'font:700 12px ' + FONT + ';color:#fff0cb;white-space:nowrap;';
  }

  private playPop(stage: number): void {
    const rm = this.motionQuery?.matches === true;
    const a = rm ? this.pops[2] : this.pops[stage - LINK_MIN_STAGE];
    for (const o of this.pops) if (o !== a) o.cancel();
    restart(a);
  }

  private open(stage: number, mult: number | undefined): void {
    this.render(stage, mult);
    if (this.shown === 0) this.box.style.display = 'block';
    this.shown = stage;
    this.lastOp = 20;
    this.box.style.opacity = '1';
    this.playPop(stage);
  }

  private close(): void {
    this.shown = 0;
    this.lastOp = -1;
    this.box.style.display = 'none';
    for (const a of this.pops) a.cancel();
    for (const a of this.sparkAnims) a.cancel();
  }

  /** HUD가 읽은 이벤트를 넘겨받는다 */
  onEvent(e: GameEvent): void {
    if (e.type === Ev.LinkUp) {
      const stage = Math.min(Math.max(Math.round(e.a), LINK_MIN_STAGE), LINK.maxStage);
      this.open(stage, e.c);
    } else if (e.type === Ev.LinkFinish) {
      this.finish();
    }
  }

  private finish(): void {
    const calm = this.motionQuery?.matches === true;
    if (!calm) for (const a of this.sparkAnims) restart(a);
    if (this.view.settings.reduceFlash) return;
    const now = performance.now();
    if (now - this.lastEdgeMs < FLASH_SAFETY.minGapMs) return;
    this.lastEdgeMs = now;
    restart(this.edgeAnim);
  }

  /** 매 프레임. u: 디자인 해상도 배율, live: 던전 전투 중 */
  update(u: number, live: boolean): void {
    if (u !== this.lastScale) {
      this.lastScale = u;
      this.box.style.transform = 'translateX(-50%) scale(' + Math.min(1, Math.max(.8, u)).toFixed(3) + ')';
    }
    const lk = this.view.link;
    const stage = linkShownStage(live, lk ? lk.stage : 0);
    if (stage === 0) {
      if (this.shown !== 0) this.close();
      return;
    }
    if (stage !== this.shown) this.open(stage, undefined);
    const op = linkOpacityStep(lk.timer);
    if (op !== this.lastOp) {
      this.lastOp = op;
      this.box.style.opacity = OPACITY[op];
    }
  }
}
